import React, { useState, useEffect } from "react";
import { Button, Box, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import SendEmailModal from "../modal/SendEmailModal";
import Client from "../../model/client";
import CloseIcon from "@mui/icons-material/Close";
import ZillowIcon from "../../assets/admin/zillow-icon.svg";
import ApiService from "../../service/ApiService";
import MapIcon from "../../assets/admin/google-maps-icon.svg";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DatabaseEntities } from "../../model/enum/database-entities.enum";
import useStyles from "../modal/styles";
import Misc from "../../utils/Misc";
import { PrimaryColor } from "../../shared/styles/colors";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "45rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  maxHeight: "90vh", // Restrict maximum height to 80% of the viewport height
  overflowY: "auto", // Enable vertical scrolling
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  clientData?: Client;
}

const ClientInfoModal = ({ clientData }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [client, setClient] = useState<Client>();
  const [toggleMore, setToggleMore] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!clientData || !clientData.id) return;
      // Fetch client data from the API
      await ApiService.get<Client>(
        `${DatabaseEntities.CLIENTS}/${clientData.id}`
      ).then((data) => {
        setClient(data);
      });
    };

    if (open && !client) fetchData();
  });

  return (
    <>
      <div>
        <Button onClick={handleOpen}>
          <Typography className={classes.headerText}>
            {clientData?.firstName} {clientData?.lastName}
          </Typography>
        </Button>
        {client && (
          <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            sx={{ overflowY: "auto" }}
          >
            <Box sx={style}>
              <div className={classes.headerDiv}>
                <Typography className={classes.title}>Client info</Typography>
                <div className={classes.headerSubDiv}>
                  {client?.street && (
                    <Button>
                      <a
                        href={`http://maps.google.com/?q=${client.street}, ${client.city}, ${client.state} ${client.zip}`}
                        target="_blank"
                      >
                        <img
                          src={MapIcon}
                          alt="google map icon"
                          style={{ width: "15px" }}
                        />
                      </a>
                    </Button>
                  )}
                  <Button onClick={handleClose} sx={{ color: "black" }}>
                    <CloseIcon />
                  </Button>
                </div>
              </div>
              <hr />
              <div className={classes.form}>
                <div className={classes.groupedItems}>
                  <Typography className={classes.label}>Id</Typography>
                  <Typography className={classes.input}>{client.id}</Typography>
                </div>
                <div className={classes.groupedItems}>
                  <Typography className={classes.label}>Name</Typography>
                  <Typography className={classes.input}>
                    {client.firstName} {client.lastName}
                  </Typography>
                </div>
                <div className={classes.inputGroup}>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>Email</Typography>
                    <Typography className={classes.input}>
                      {client.email}
                    </Typography>
                  </div>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>Phone</Typography>
                    <a href={"tel:+" + client.phone} className="ui-link">
                      <Typography className={classes.input}>
                        {Misc.formatPhoneNumber(client.phone)}
                      </Typography>
                    </a>
                  </div>
                </div>
                <div className={classes.inputGroup}>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>Street</Typography>
                    <Typography className={classes.input}>
                      {client?.street}
                    </Typography>
                  </div>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>Zip code</Typography>
                    <Typography className={classes.input}>
                      {client?.zip}
                    </Typography>
                  </div>
                </div>
                <div className={classes.inputGroup}>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>City</Typography>
                    <Typography className={classes.input}>
                      {client?.city}
                    </Typography>
                  </div>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>State</Typography>
                    <Typography className={classes.input}>
                      {client?.state}
                    </Typography>
                  </div>
                </div>
                <div className={classes.inputGroup}>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>
                      Client Type
                    </Typography>
                    <Typography className={classes.input}>
                      {client?.ClientType?.name}
                    </Typography>
                  </div>
                  <div className={classes.groupedItems}>
                    <Typography className={classes.label}>
                      Client Level
                    </Typography>
                    <Typography className={classes.input}>
                      {client?.ClientLevel?.name}
                    </Typography>
                  </div>
                </div>
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Button onClick={() => setToggleMore(!toggleMore)}>
                  {toggleMore ? (
                    <ExpandLessIcon sx={{ color: PrimaryColor }} />
                  ) : (
                    <ExpandMoreIcon sx={{ color: PrimaryColor }} />
                  )}
                </Button>
              </div>
              <div
                style={{
                  transition: "max-height 0.9s ease-in-out",
                  overflow: "hidden",
                  maxHeight: toggleMore ? "1000px" : "0",
                }}
              >
                {toggleMore && (
                  <>
                    <div className={classes.inputGroup}>
                      <div className={classes.groupedItems}>
                        <Typography className={classes.label}>
                          Alternative Name
                        </Typography>
                        <Typography className={classes.input}>
                          {client.alternativeName}
                        </Typography>
                      </div>
                      <div className={classes.groupedItems}>
                        <Typography className={classes.label}>
                          Alternative Phone
                        </Typography>
                        <Typography className={classes.input}>
                          {client.alternativePhone &&
                            Misc.formatPhoneNumber(client?.alternativePhone)}
                        </Typography>
                      </div>
                    </div>
                    <div className={classes.inputGroup}>
                      <div className={classes.groupedItems}>
                        <Typography className={classes.label}>
                          Alternative Email
                        </Typography>
                        <Typography className={classes.input}>
                          {client.alternativeEmail}
                        </Typography>
                      </div>
                    </div>
                    <div className={classes.groupedItems}>
                      <Typography className={classes.label}>Notes</Typography>
                      <textarea
                        className={classes.input}
                        value={client?.notes}
                        disabled
                      />
                    </div>
                  </>
                )}
              </div>
              <div className={classes.buttonGroup}>
                <SendEmailModal
                  name={client?.firstName}
                  email={client?.email}
                />
                {client?.street && (
                  <Btn
                    className={classes.button}
                    onClick={() =>
                      window.open(
                        `https://www.zillow.com/homes/${client.street}, ${client.city}, ${client.state} ${client.zip}`
                      )
                    }
                  >
                    <img src={ZillowIcon} alt="zillow icon" />
                    <Typography style={{ marginLeft: "5%" }}>Zillow</Typography>
                  </Btn>
                )}
              </div>
            </Box>
          </Modal>
        )}
      </div>
    </>
  );
};

export default ClientInfoModal;
